import * as Sentry from "@sentry/react"

import { UploadStatus } from "#customTypes/composedTypes/firestoreTypes"

export const ARTWORK_UPLOAD_PROCESSING_ERROR =
  "Unknown error while processing artwork upload"

/**
 * `heic2any` rejects with `{ code, message }`, not an `Error`. Sentry must
 * receive that object (or a real Error). The generic sentence is only for
 * the toast.
 */
type HeicLibraryRejection = {
  code: number
  message: string
}

const isHeicLibraryRejection = (
  rejection: unknown
): rejection is HeicLibraryRejection => {
  if (typeof rejection !== "object" || rejection === null) return false
  if (!("code" in rejection) || !("message" in rejection)) return false

  return (
    typeof rejection.code === "number" && typeof rejection.message === "string"
  )
}

export const reportHeicConversionFailure = (rejection: unknown): void => {
  if (rejection instanceof Error) {
    Sentry.captureException(rejection)
    return
  }

  if (isHeicLibraryRejection(rejection)) {
    Sentry.captureException(rejection, {
      extra: { code: rejection.code, message: rejection.message },
    })
    return
  }

  Sentry.captureException(rejection)
}

export class HeicConversionFailure extends Error {
  constructor(rejection: unknown) {
    super(ARTWORK_UPLOAD_PROCESSING_ERROR, { cause: rejection })
    this.name = "HeicConversionFailure"
  }
}

export const isHeicConversionFailure = (
  error: unknown
): error is HeicConversionFailure => error instanceof HeicConversionFailure

/** What the Dragger catch shows. Conversion failures stay on the generic line. */
export const artworkUploadFailureMessage = (err: unknown): string => {
  if (isHeicConversionFailure(err)) return ARTWORK_UPLOAD_PROCESSING_ERROR
  if (err instanceof Error) return err.message || "Artwork upload failed"
  return ARTWORK_UPLOAD_PROCESSING_ERROR
}

/**
 * Ant Design toasts `${filename} upload failed.` from Upload `onChange` when
 * `customRequest` calls `onError`. Skip that for a conversion failure so the
 * generic sentence is the only toast.
 */
export const filenameUploadFailedToast = (
  status: string | undefined,
  error: unknown,
  filename: string
): string | undefined => {
  if (status !== UploadStatus.ERROR || isHeicConversionFailure(error)) {
    return undefined
  }

  return `${filename} upload failed.`
}
